// frontend/src/components/FastReact/RestockGroups.jsx
//
// Dedicated RESTOCK menu screen. Unlike Plano Teares (which lists individual
// po_setup rows), this starts with preparation lines status_line='RESTOCK'
// grouped by product_code+variant_code+lot_no across ALL po_setups, then
// expands per group into the same line-by-line actions as Plano Teares.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fastReactAPI } from '../../api/client';
import Toast from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import { FiRefreshCw, FiChevronDown, FiChevronRight, FiPlay, FiRotateCcw } from 'react-icons/fi';
import './PlanoTeares.css'; // reuses pt-* classes (badges, timer, prep table, etc.)

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  const dd = String(dt.getDate()).padStart(2, '0');
  const mo = String(dt.getMonth() + 1).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return `${dd}-${mo}-${dt.getFullYear()} ${hh}:${mi}`;
};

// Same static (non-ticking) SLA timer as Plano Teares — computed once per
// render pass, not on an interval. See PlanoTeares.jsx for the full rationale.
const RESTOCK_SLA_MINUTES = 5 * 60; // 5h00
const formatRestockTimer = (restockRequestAt) => {
  if (!restockRequestAt) return null;
  const elapsedMs = Date.now() - new Date(restockRequestAt).getTime();
  const remainingMin = RESTOCK_SLA_MINUTES - Math.floor(elapsedMs / 60000);
  const overdue = remainingMin < 0;
  const abs = Math.abs(remainingMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return { text: `${overdue ? '-' : ''}${hh}:${mm}`, overdue };
};

const prepStatusBadge = (statusLine) => {
  if (!statusLine) return null;
  if (statusLine === 'RESTOCK') return <span className="status-badge status--restock">Reabastecer</span>;
  return <span className="status-badge">{statusLine}</span>;
};

const groupKey = (g) => `${g.product_code}::${g.variant_code ?? ''}::${g.lot_no ?? ''}`;

const RestockGroups = () => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedKey, setExpandedKey] = useState(null);
  const [itemsByGroup, setItemsByGroup] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);

  // ── Filters: po_code / product_code / variant_code / lot_no ────────────────
  // Same debounced free-text pattern as Plano Teares — each keystroke resets
  // a short timer before actually re-querying the server.
  const [filters, setFilters] = useState({
    po_code: '', product_code: '', variant_code: '', lot_no: '',
  });
  const [poInput, setPoInput] = useState('');
  const [prodInput, setProdInput] = useState('');
  const [varInput, setVarInput] = useState('');
  const [lotInput, setLotInput] = useState('');
  const poDebounce = useRef(null);
  const prodDebounce = useRef(null);
  const varDebounce = useRef(null);
  const lotDebounce = useRef(null);

  const loadGroups = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (f.po_code) params.po_code = f.po_code;
      if (f.product_code) params.product_code = f.product_code;
      if (f.variant_code) params.variant_code = f.variant_code;
      if (f.lot_no) params.lot_no = f.lot_no;
      const r = await fastReactAPI.getRestockGroups(params);
      if (r.data.success) setGroups(r.data.groups);
      else setError(r.data.error || 'Erro ao carregar reabastecimentos.');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao carregar reabastecimentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(filters); }, [loadGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    const newF = { ...filters, [key]: value };
    setFilters(newF);
    setExpandedKey(null);
    loadGroups(newF);
  };

  const loadGroupItems = useCallback(async (g) => {
    setLoadingItems(true);
    try {
      const params = new URLSearchParams({ product_code: g.product_code });
      if (g.variant_code) params.append('variant_code', g.variant_code);
      if (g.lot_no) params.append('lot_no', g.lot_no);
      const r = await fastReactAPI.getRestockGroupItems(params.toString());
      if (r.data.success) {
        setItemsByGroup((prev) => ({ ...prev, [groupKey(g)]: r.data.items }));
      }
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao carregar linhas do grupo.');
    } finally {
      setLoadingItems(false);
    }
  }, [toast_error]);

  const handleExpand = (g) => {
    const key = groupKey(g);
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (!itemsByGroup[key]) loadGroupItems(g);
  };

  // After any action, the line stops being RESTOCK — its group's aggregates
  // (cones sum, cluster/machine counts, oldest timer) all potentially change,
  // and if it was the last line in the group, the group disappears entirely.
  // Reloading both the groups list and the expanded group's items from the
  // server (rather than patching state locally) keeps this correct without
  // hand-rolled aggregate math that could drift out of sync.
  const refreshAfterAction = async (g) => {
    await loadGroups(filters);
    if (g) await loadGroupItems(g);
  };

  const handleAdvance = async (g, preparationId) => {
    try {
      const r = await fastReactAPI.advancePreparationStatus(preparationId, null);
      if (!r.data.success) return;
      toast_success('Reabastecimento iniciado/concluído.');
      await refreshAfterAction(g);
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao avançar estado.');
    }
  };

  const handleRevert = async (g, preparationId) => {
    try {
      const r = await fastReactAPI.revertPreparationStatus(preparationId);
      if (!r.data.success) return;
      await refreshAfterAction(g);
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao reverter estado.');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header page-header--compact">
        <h2>{t('restock.title', 'Reabastecer')}</h2>
        <div className="page-header-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => loadGroups(filters)}
            disabled={loading}
            title="Atualizar"
          >
            <FiRefreshCw size={14} className={loading ? 'pt-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="list-filters">
        <div className="filters-row">
          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.ordem', 'Ordem')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={poInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setPoInput(v);
                  clearTimeout(poDebounce.current);
                  poDebounce.current = setTimeout(() => handleFilterChange('po_code', v), 350);
                }}
              />
              {poInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setPoInput('');
                    clearTimeout(poDebounce.current);
                    handleFilterChange('po_code', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.product_code', 'Produto')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={prodInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setProdInput(v);
                  clearTimeout(prodDebounce.current);
                  prodDebounce.current = setTimeout(() => handleFilterChange('product_code', v), 350);
                }}
              />
              {prodInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setProdInput('');
                    clearTimeout(prodDebounce.current);
                    handleFilterChange('product_code', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.variant_code', 'Variante')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={varInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setVarInput(v);
                  clearTimeout(varDebounce.current);
                  varDebounce.current = setTimeout(() => handleFilterChange('variant_code', v), 350);
                }}
              />
              {varInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setVarInput('');
                    clearTimeout(varDebounce.current);
                    handleFilterChange('variant_code', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.lot_no', 'Lote')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={lotInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setLotInput(v);
                  clearTimeout(lotDebounce.current);
                  lotDebounce.current = setTimeout(() => handleFilterChange('lot_no', v), 350);
                }}
              />
              {lotInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setLotInput('');
                    clearTimeout(lotDebounce.current);
                    handleFilterChange('lot_no', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll">
          {loading ? (
            <div className="pt-state-msg">A carregar…</div>
          ) : error ? (
            <div className="pt-state-msg pt-state-error">{error}</div>
          ) : !groups.length ? (
            <div className="pt-state-msg">Sem registos para reabastecer.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Produto</th>
                  <th style={{ width: 100 }}>Variante</th>
                  <th style={{ width: 150 }}>Lote</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Cones</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Núcleos</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Máquinas</th>
                  <th style={{ width: 90 }}>Timer SLA</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const key = groupKey(g);
                  const isExp = expandedKey === key;
                  const items = itemsByGroup[key] || [];
                  const timer = formatRestockTimer(g.oldest_restock_request_at);

                  return (
                    <React.Fragment key={key}>
                      <tr className="clickable" onClick={() => handleExpand(g)}>
                        <td className="pt-td-toggle">
                          {isExp ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                        </td>
                        <td>
                          <span className="pt-prep-code">{g.product_code}</span>
                          {g.product_description && (
                            <span className="pt-prep-desc">{g.product_description}</span>
                          )}
                        </td>
                        <td className="text-muted">{g.variant_code || '—'}</td>
                        <td className="pt-mono">{g.lot_no || '—'}</td>
                        <td className="text-right pt-qty">{g.total_cones ?? '—'}</td>
                        <td className="text-right pt-mono">{g.cluster_count}</td>
                        <td className="text-right pt-mono">{g.machine_count}</td>
                        <td>
                          {timer ? (
                            <span className={`pt-restock-timer ${timer.overdue ? 'pt-restock-timer--overdue' : ''}`}>
                              {timer.text}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>

                      {isExp && (
                        <tr className="pt-expand-tr">
                          <td className="pt-expand-td" colSpan={8}>
                            <div className="pt-expand-inner">
                              {loadingItems && !items.length ? (
                                <p className="pt-prep-msg">A carregar…</p>
                              ) : !items.length ? (
                                <p className="pt-prep-msg">Sem linhas.</p>
                              ) : (
                                <table className="pt-prep-table">
                                  <thead>
                                    <tr>
                                      <th style={{ width: 76 }}></th>
                                      <th style={{ width: 130 }}>PO</th>
                                      <th style={{ width: 70 }}>Núcleo</th>
                                      <th style={{ width: 60 }}>Máq.</th>
                                      <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
                                      <th style={{ width: 100 }}>Estado</th>
                                      <th style={{ width: 80 }}>Cones</th>
                                      <th style={{ width: 90 }}>Carrinho</th>
                                      <th style={{ width: 110 }}>Localização</th>
                                      <th>Reabastecido por</th>
                                      <th style={{ width: 130 }}>Data reab.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item) => (
                                      <tr key={item.preparation_id}>
                                        <td>
                                          <div className="pt-actions-row">
                                            <button
                                              type="button"
                                              className="pt-advance-btn"
                                              onClick={(e) => { e.stopPropagation(); handleAdvance(g, item.preparation_id); }}
                                              title="Iniciar reabastecimento"
                                            >
                                              <FiPlay size={15} />
                                            </button>
                                            <button
                                              type="button"
                                              className="pt-advance-btn pt-advance-btn--undo"
                                              onClick={(e) => { e.stopPropagation(); handleRevert(g, item.preparation_id); }}
                                              title="Desfazer"
                                            >
                                              <FiRotateCcw size={15} />
                                            </button>
                                          </div>
                                        </td>
                                        <td className="pt-mono">{item.po_code_line_full}</td>
                                        <td>
                                          {item.machine_cluster ? (
                                            <span className="pt-cluster-badge" style={{ fontSize: 10 }}>{item.machine_cluster}</span>
                                          ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                          {item.machine ? (
                                            <span className="pt-linha-badge" style={{ fontSize: 10 }}>{item.machine}</span>
                                          ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td className="text-right pt-mono">
                                          {item.quantity != null ? parseFloat(item.quantity).toFixed(1) : '—'}
                                        </td>
                                        <td>{prepStatusBadge(item.status_line)}</td>
                                        <td className="text-right pt-mono">{item.num_restock_cones ?? '—'}</td>
                                        <td className="pt-mono">{item.container_material || '—'}</td>
                                        <td className="pt-mono" style={{ fontSize: 11 }}>{item.location_position || '—'}</td>
                                        <td className="text-muted">{item.restock_by_name || '—'}</td>
                                        <td className="pt-date">{item.restock_at ? formatDate(item.restock_at) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>
    </div>
  );
};

export default RestockGroups;